import categories from '@content/categories.json';
import { markdownToHtml, toExcerpt } from './seo';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface GameTip {
  title: string;
  tip: string;
}

export interface Game {
  slug: string;
  title: string;
  description: string;
  shortIntro?: string;
  fullDescription?: string;
  howToPlay?: string;
  categories: string[];
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  technology?: string;
  platforms?: string;
  publishedDate?: string;
  lastUpdated?: string;
  isFeatured?: boolean;
  isTrending?: boolean;
  tags?: string[];
  faqs?: FAQItem[];
  gameTips?: GameTip[];
  gameStory?: string;
  jsonLd?: Record<string, unknown>;
}

export interface Category {
  slug: string;
  name: string;
  description?: string;
}

export interface StaticPage {
  slug: string;
  title: string;
  excerpt?: string;
  html: string;
}

export interface HomePage {
  title: string;
  shortIntro: string;
  seoContent: string;
  metaDescription: string;
  metaKeywords: string;
}

type ServerContentModule = {
  getFeaturedGames: () => Game[];
  getTrendingGames?: () => Game[];
  getGameBySlug: (slug: string) => Game | undefined;
  getGamesByCategory: (slug: string) => Game[];
  getCategoryBySlug: (slug: string) => Category | undefined;
  searchGames: (query: string) => Game[];
  getPageBySlug: (slug: string) => StaticPage | undefined;
  getAllRoutes: () => string[];
  getLatestGames?: (limit?: number) => Game[];
};

const getServerContent = (): ServerContentModule | undefined =>
  typeof window === 'undefined'
    ? (globalThis as unknown as { __contentServer?: ServerContentModule }).__contentServer
    : undefined;

let gameIndex: Game[] | null = null;
let categoryIndex: Category[] | null = null;
let pageIndex: StaticPage[] | null = null;
let homePageContent: HomePage | null = null;

function loadIndexes() {
  if (gameIndex && categoryIndex && pageIndex) return;

  const gameModules = import.meta.glob<{ default: Game }>('@content/games/*.json', { eager: true });
  gameIndex = Object.entries(gameModules)
    .filter(([filePath]) => !filePath.includes('_template.json'))
    .map(([, mod]) => mod.default)
    .sort((a, b) => {
      // Sort by publishedDate (newest first)
      const dateA = a.publishedDate ? new Date(a.publishedDate).getTime() : 0;
      const dateB = b.publishedDate ? new Date(b.publishedDate).getTime() : 0;
      return dateB - dateA;
    });

  categoryIndex = categories;

  const pageModules = import.meta.glob<string>('@content/pages/*.md', { eager: true, query: '?raw', import: 'default' });
  pageIndex = Object.entries(pageModules).map(([filePath, markdown]) => {
    const slug = filePath.split('/').pop()!.replace(/\.md$/, '');
    const html = markdownToHtml(markdown);
    return {
      slug,
      title: slug === 'privacy' ? 'Privacy Policy' : slug === 'terms' ? 'Terms of Service' : slug,
      excerpt: toExcerpt(markdown),
      html
    };
  });

  // Load home page content
  try {
    const homeModule = import.meta.glob<{ default: HomePage }>('@content/pages/home.json', { eager: true });
    const homeEntry = Object.entries(homeModule)[0];
    if (homeEntry) {
      homePageContent = homeEntry[1].default;
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('home.json not found');
    }
  }
}

export function getFeaturedGames(): Game[] {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getFeaturedGames();
  loadIndexes();
  return (gameIndex ?? []).filter((g) => g.isFeatured === true);
}

export function getTrendingGames(): Game[] {
  const serverContent = getServerContent();
  if (serverContent?.getTrendingGames) return serverContent.getTrendingGames();
  loadIndexes();
  return (gameIndex ?? []).filter((g) => g.isTrending === true);
}

export function getGameBySlug(slug: string): Game | undefined {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getGameBySlug(slug);
  loadIndexes();
  return gameIndex?.find((g) => g.slug === slug);
}

export function getGamesByCategory(slug: string): Game[] {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getGamesByCategory(slug);
  loadIndexes();
  return (gameIndex ?? []).filter((g) => g.categories?.includes(slug));
}

export function getLatestGames(limit?: number): Game[] {
  const serverContent = getServerContent();
  if (serverContent?.getLatestGames) return serverContent.getLatestGames(limit);
  loadIndexes();
  // gameIndex is already sorted by publishedDate (newest first)
  const games = gameIndex ?? [];
  return limit ? games.slice(0, limit) : games;
}

export function getCategoryBySlug(slug: string): Category | undefined {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getCategoryBySlug(slug);
  loadIndexes();
  return categoryIndex?.find((c) => c.slug === slug);
}

export function searchGames(query: string): Game[] {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.searchGames(query);
  loadIndexes();
  if (!query.trim()) return gameIndex ?? [];
  const term = query.toLowerCase();
  return (gameIndex ?? []).filter((g) => g.title.toLowerCase().includes(term) || g.description.toLowerCase().includes(term));
}

export function getPageBySlug(slug: string): StaticPage | undefined {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getPageBySlug(slug);
  loadIndexes();
  return pageIndex?.find((p) => p.slug === slug);
}

export function getHomePageContent(): HomePage | null {
  loadIndexes();
  return homePageContent;
}

export function getAllRoutes(): string[] {
  const serverContent = getServerContent();
  if (serverContent) return serverContent.getAllRoutes();
  loadIndexes();
  return [
    '/',
    '/search',
    '/privacy',
    '/terms',
    ...(categoryIndex ?? []).map((c) => `/category/${c.slug}`),
    ...(gameIndex ?? []).map((g) => `/${g.slug}`)
  ];
}
